import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AudioLines, Check, Expand, Loader2, MessageSquare, MessageSquareOff, Mic, MicOff, RotateCcw, Send, Shrink, Sparkles, Volume2, VolumeX, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition as NativeSpeech } from "@capacitor-community/speech-recognition";
import { speakWithDevice, stopDeviceSpeech, voiceSupported } from "@/lib/deviceVoice";

// Android's System WebView has no `webkitSpeechRecognition`; the installed
// app uses the Capacitor community plugin. Web / desktop keep the Web
// Speech API. Listening is free either way.
const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Session 32/33 — GPT Copilot (Owner / Manager dashboard).
 *
 *  • Brain   : gpt-4.1-mini on the server (`POST /ai/chat`, streamed as SSE)
 *  • Ears    : browser / native microphone (free)
 *  • Voice   : the device voice (free, user-picked under Settings → AI).
 *              OpenAI TTS is only tried when the server says it is available.
 *  • Safety  : any price/stock change arrives as a `confirm` card — nothing is
 *              written until the user taps Apply or says yes.
 *  • Modes   : "full" (chat panel, top-right) and "mini" (voice-only pill —
 *              the chat is hidden so the dashboard stays visible while you
 *              talk). The launcher lives in the dashboard header
 *              (`#ai-copilot-slot`) so no floating button covers content.
 */
export const AiAssistant = ({
  http,
  authHeaders,
  context,          // { settings, usage, voices, model }
  onNavigate,
  getScreen,
  stockTakeLines,
  onStockTakeSet,
  onStockTakeSave,
  onUsage,          // (usage) => void — lets the dashboard mirror the meter
}) => {
  const [mode, setMode] = useState("closed"); // closed | full | mini
  const [large, setLarge] = useState(false);
  const [messages, setMessages] = useState([]); // {id, role, text, streaming?, tools?}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [pending, setPending] = useState(null); // {token, summary, changes, problems}
  const [handsFree, setHandsFree] = useState(false);
  const [muted, setMuted] = useState(false);
  const [usage, setUsage] = useState(context?.usage || null);
  const [slot, setSlot] = useState(null);

  const isOpen = mode !== "closed";
  const recogRef = useRef(null);
  const listRef = useRef(null);
  const audioRef = useRef(null);
  const handsFreeRef = useRef(false);
  const openRef = useRef(false);
  const stockLinesRef = useRef(stockTakeLines);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const openAiDeadRef = useRef(false); // OpenAI voice failed this session → device voice only
  useEffect(() => { stockLinesRef.current = stockTakeLines; }, [stockTakeLines]);
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
  useEffect(() => { openRef.current = isOpen; }, [isOpen]);
  useEffect(() => { if (context?.usage) setUsage(context.usage); }, [context?.usage]);
  useEffect(() => { setSlot(typeof document !== "undefined" ? document.getElementById("ai-copilot-slot") : null); }, []);

  const settings = context?.settings || {};
  const SpeechRecognitionCtor = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const [nativeAvailable, setNativeAvailable] = useState(false);
  useEffect(() => {
    if (!IS_NATIVE) return;
    NativeSpeech.available().then((r) => setNativeAvailable(!!r?.available)).catch(() => setNativeAvailable(false));
  }, []);
  const useNativeSpeech = IS_NATIVE && nativeAvailable;
  const speechInputSupported = useNativeSpeech || !!SpeechRecognitionCtor;
  const browserVoiceSupported = voiceSupported();
  const speakOn = !muted && settings.speak_replies !== false;
  const openAiVoiceOn = speakOn && settings.tts_available === true;

  const apiBase = useMemo(() => (http?.defaults?.baseURL || "").replace(/\/$/, ""), [http]);

  // ── speaking ────────────────────────────────────────────────────────
  const startListeningRef = useRef(() => {});
  const stopSpeaking = useCallback(() => {
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; } } catch (_) { /* noop */ }
    stopDeviceSpeech();
    setSpeaking(false);
  }, []);

  const afterSpeech = useCallback((thenListen) => {
    setSpeaking(false);
    if (thenListen && handsFreeRef.current && openRef.current) startListeningRef.current();
  }, []);

  const speakBrowser = useCallback((text, thenListen) => {
    if (!browserVoiceSupported) { afterSpeech(thenListen); return; }
    speakWithDevice(text, { onStart: () => setSpeaking(true) }).then(() => afterSpeech(thenListen));
  }, [browserVoiceSupported, afterSpeech]);

  const speak = useCallback(async (text, { thenListen = false } = {}) => {
    const clean = String(text || "").trim();
    if (!clean || !speakOn) { afterSpeech(thenListen); return; }
    if (openAiVoiceOn && !openAiDeadRef.current) {
      try {
        const res = await fetch(`${apiBase}/ai/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(authHeaders || {}) },
          body: JSON.stringify({ text: clean.slice(0, 4000) }),
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const audio = audioRef.current || new Audio();
          audioRef.current = audio;
          audio.src = url;
          audio.onplay = () => setSpeaking(true);
          audio.onended = () => { URL.revokeObjectURL(url); afterSpeech(thenListen); };
          audio.onerror = () => { URL.revokeObjectURL(url); speakBrowser(clean, thenListen); };
          await audio.play();
          return;
        }
        // Quietly fall back to the device voice for the rest of this session.
        openAiDeadRef.current = true;
      } catch (_) {
        openAiDeadRef.current = true;
      }
    }
    speakBrowser(clean, thenListen);
  }, [speakOn, openAiVoiceOn, apiBase, authHeaders, afterSpeech, speakBrowser]);

  // ── transcript helpers ──────────────────────────────────────────────
  const pushMessage = useCallback((role, text, extra = {}) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setMessages((m) => [...m.slice(-60), { id, role, text, ...extra }]);
    return id;
  }, []);
  const patchMessage = useCallback((id, fn) => {
    setMessages((m) => m.map((x) => (x.id === id ? fn(x) : x)));
  }, []);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, pending, interim, mode]);

  const applyAction = useCallback((action) => {
    if (!action) return;
    if (action.type === "navigate") {
      try { onNavigate && onNavigate(action); } catch (_) { /* ignore nav errors */ }
    } else if (action.type === "stock_take_set") {
      onStockTakeSet && onStockTakeSet(action);
    } else if (action.type === "stock_take_save") {
      onStockTakeSave && onStockTakeSave();
    } else if (action.type === "changes_applied") {
      if (action.applied?.length) toast.success(`${action.applied.length} change${action.applied.length === 1 ? "" : "s"} applied`);
      if (action.failed?.length) toast.error(`${action.failed.length} change${action.failed.length === 1 ? "" : "s"} failed`);
      setPending(null);
    }
  }, [onNavigate, onStockTakeSet, onStockTakeSave]);

  // ── the round-trip (SSE over fetch) ─────────────────────────────────
  const ask = useCallback(async (text, inputMode = "text", { reset = false } = {}) => {
    const q = String(text || "").trim();
    if (!q || busy) return;
    stopSpeaking();
    setInput("");
    setInterim("");
    pushMessage("user", q, { mode: inputMode });
    const aid = pushMessage("assistant", "", { streaming: true, tools: [] });
    setBusy(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let reply = "";
    let hadError = false;
    try {
      const body = {
        message: q,
        input_mode: inputMode,
        screen: typeof getScreen === "function" ? getScreen() : null,
        stock_take_lines: stockLinesRef.current ? stockLinesRef.current.map((l) => ({ name: l.name || l.item_name, item_id: l.item_id || l.id, item_type: l.item_type || l.type, unit: l.unit, expected: l.expected, actual: l.actual })) : null,
        reset,
      };
      const res = await fetch(`${apiBase}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...(authHeaders || {}) },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        let detail = "The assistant is unavailable right now.";
        try { const j = await res.json(); detail = typeof j?.detail === "string" ? j.detail : detail; } catch (_) { /* noop */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch (_) { continue; }
          if (ev.type === "delta") {
            reply += ev.text || "";
            patchMessage(aid, (x) => ({ ...x, text: reply }));
          } else if (ev.type === "tool") {
            patchMessage(aid, (x) => ({ ...x, tools: [...(x.tools || []), ev.name] }));
          } else if (ev.type === "action") {
            applyAction(ev.action);
          } else if (ev.type === "confirm") {
            setPending({ token: ev.token, summary: ev.summary, changes: ev.changes || [], problems: ev.problems || [] });
          } else if (ev.type === "done") {
            reply = ev.reply || reply;
            if (ev.usage) { setUsage(ev.usage); onUsage && onUsage(ev.usage); }
            patchMessage(aid, (x) => ({ ...x, text: reply, streaming: false, capped: !!ev.capped }));
          } else if (ev.type === "error") {
            hadError = true;
            reply = ev.message || "Something went wrong.";
            patchMessage(aid, (x) => ({ ...x, text: reply, streaming: false, error: true }));
          }
        }
      }
      patchMessage(aid, (x) => ({ ...x, streaming: false }));
    } catch (error) {
      hadError = true;
      const msg = error?.name === "AbortError" ? "Stopped." : (error?.message || "The assistant is unavailable right now.");
      reply = msg;
      patchMessage(aid, (x) => ({ ...x, text: msg, streaming: false, error: true }));
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
    if (reply && !hadError) speak(reply, { thenListen: inputMode === "voice" });
    else if (handsFreeRef.current && openRef.current) setTimeout(() => startListeningRef.current(), 400);
  }, [busy, apiBase, authHeaders, getScreen, pushMessage, patchMessage, applyAction, speak, stopSpeaking, onUsage]);

  const confirmPending = useCallback(async (accepted) => {
    if (!pending) return;
    const token = pending.token;
    setPending(null);
    setBusy(true);
    try {
      const r = await http.post("/ai/confirm", { token, accepted }, { headers: authHeaders });
      const reply = r.data?.reply || (accepted ? "Done." : "Cancelled.");
      pushMessage("assistant", reply, { applied: r.data?.applied || [], failed: r.data?.failed || [] });
      if (accepted && r.data?.applied?.length) toast.success(`${r.data.applied.length} change${r.data.applied.length === 1 ? "" : "s"} applied`);
      if (r.data?.failed?.length) toast.error(`${r.data.failed.length} failed`);
      speak(reply, { thenListen: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not apply that.";
      pushMessage("assistant", msg, { error: true });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [pending, http, authHeaders, pushMessage, speak]);

  const resetConversation = useCallback(async () => {
    stopSpeaking();
    setPending(null);
    setMessages([]);
    try { await http.post("/ai/reset", {}, { headers: authHeaders }); } catch (_) { /* noop */ }
  }, [http, authHeaders, stopSpeaking]);

  // ── listening ───────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    const r = recogRef.current;
    if (r === "native") { recogRef.current = null; NativeSpeech.stop().catch(() => {}); }
    else if (r) { try { r.onend = null; r.stop(); } catch (_) { /* noop */ } recogRef.current = null; }
    setListening(false);
    setInterim("");
  }, []);

  const startListeningNative = useCallback(async () => {
    if (recogRef.current) return;
    stopSpeaking();
    try {
      let perm = await NativeSpeech.checkPermissions();
      if (perm?.speechRecognition !== "granted") perm = await NativeSpeech.requestPermissions();
      if (perm?.speechRecognition !== "granted") {
        toast.error("Microphone access is needed for voice. Allow it in the app's permissions.");
        setHandsFree(false);
        return;
      }
      recogRef.current = "native";
      setListening(true);
      const res = await NativeSpeech.start({ language: "en-ZA", maxResults: 1, partialResults: false, popup: false });
      const wasCancelled = recogRef.current !== "native";
      recogRef.current = null;
      setListening(false);
      const heard = (res?.matches?.[0] || "").trim();
      if (heard && !wasCancelled) ask(heard, "voice");
      else if (!wasCancelled && handsFreeRef.current && openRef.current) setTimeout(() => { if (!recogRef.current) startListeningRef.current(); }, 400);
    } catch (error) {
      recogRef.current = null;
      setListening(false);
      const msg = String(error?.message || error || "");
      if (!/no match|timeout|cancel|abort/i.test(msg)) toast.error(msg || "Voice recognition failed on this device.");
      else if (handsFreeRef.current && openRef.current) setTimeout(() => { if (!recogRef.current) startListeningRef.current(); }, 400);
    }
  }, [stopSpeaking, ask]);

  const startListening = useCallback(() => {
    if (!speechInputSupported || busy || recogRef.current) return;
    if (useNativeSpeech) { startListeningNative(); return; }
    stopSpeaking();
    let recog;
    try { recog = new SpeechRecognitionCtor(); } catch (_) { toast.error("Voice input is not available in this browser."); return; }
    recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-ZA";
    recog.interimResults = true;
    recog.continuous = false;
    recog.maxAlternatives = 1;
    let finalText = "";
    recog.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript; else interimText += r[0].transcript;
      }
      setInterim(interimText || finalText);
    };
    recog.onerror = (event) => {
      const code = event?.error;
      if (code === "not-allowed" || code === "service-not-allowed") { toast.error("Microphone access is blocked. Allow the microphone to talk to the assistant."); setHandsFree(false); }
      else if (code === "audio-capture") { toast.error("No microphone was found on this device."); setHandsFree(false); }
      else if (code === "network") toast.error("Voice recognition needs an internet connection on this device.");
    };
    recog.onend = () => {
      recogRef.current = null;
      setListening(false);
      setInterim("");
      const heard = finalText.trim();
      if (heard) ask(heard, "voice");
      else if (handsFreeRef.current && openRef.current) setTimeout(() => { if (handsFreeRef.current && openRef.current && !recogRef.current) startListeningRef.current(); }, 400);
    };
    recogRef.current = recog;
    try { recog.start(); setListening(true); } catch (_) { recogRef.current = null; setListening(false); }
  }, [speechInputSupported, busy, useNativeSpeech, startListeningNative, stopSpeaking, SpeechRecognitionCtor, ask]);
  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  useEffect(() => () => { stopListening(); stopSpeaking(); if (abortRef.current) abortRef.current.abort(); }, [stopListening, stopSpeaking]);
  useEffect(() => { if (!isOpen) { stopListening(); stopSpeaking(); setHandsFree(false); } }, [isOpen, stopListening, stopSpeaking]);

  const setHandsFreeMode = useCallback((next) => {
    setHandsFree(next);
    handsFreeRef.current = next;
    if (next) { if (!recogRef.current) startListeningRef.current(); }
    else stopListening();
  }, [stopListening]);
  const toggleHandsFree = () => setHandsFreeMode(!handsFree);

  const openPanel = useCallback((which = "full") => {
    setMode(which);
    openRef.current = true;
    if (which === "full") setTimeout(() => inputRef.current?.focus(), 60);
    // Owner preference: start hands-free as soon as the copilot opens.
    if (settings.auto_listen && speechInputSupported) setTimeout(() => setHandsFreeMode(true), 120);
  }, [settings.auto_listen, speechInputSupported, setHandsFreeMode]);
  const closePanel = useCallback(() => { setMode("closed"); setLarge(false); }, []);

  const usedZar = Number(usage?.used_zar || 0);
  const capZar = Number(usage?.cap_zar || 0);
  const pctUsed = capZar > 0 ? Math.min(100, Math.round((usedZar / capZar) * 100)) : 0;

  const statusText = busy ? "Thinking…" : listening ? "Listening…" : speaking ? "Speaking…" : handsFree ? "Hands-free — say something" : (mode === "mini" ? "Voice only" : "Ask me anything about this dashboard");
  const StatusIcon = listening ? Mic : speaking ? Volume2 : busy ? Loader2 : Sparkles;
  const statusTone = listening ? "bg-rose-600 text-white" : speaking ? "bg-[hsl(var(--primary))] text-primary-foreground" : "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]";

  // ── launcher (dashboard header slot, fallback: fixed top-right) ─────
  const launcher = (
    <Button
      type="button"
      variant={isOpen ? "default" : "outline"}
      className={`relative rounded-xl ${isOpen ? "" : "border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))]"}`}
      onClick={() => (isOpen ? closePanel() : openPanel("full"))}
      aria-pressed={isOpen}
      aria-label={isOpen ? "Close the AI copilot" : "Open the AI copilot"}
      data-testid="ai-launcher"
    >
      <Sparkles className="h-4 w-4" />
      Copilot
      {isOpen && (listening || speaking || busy) ? <span className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500 ring-2 ring-card" aria-hidden="true" /> : null}
    </Button>
  );
  const launcherNode = slot ? createPortal(launcher, slot) : <div className="fixed right-3 top-3 z-[120] print:hidden">{launcher}</div>;

  // Shared header controls (mini + full).
  const micButton = speechInputSupported ? (
    <Button
      type="button"
      variant={listening ? "default" : "outline"}
      size="icon"
      className={`h-10 w-10 shrink-0 rounded-xl ${listening ? "bg-rose-600 text-white hover:bg-rose-600/90" : ""}`}
      onClick={() => (listening ? stopListening() : startListening())}
      disabled={busy}
      aria-label={listening ? "Stop listening" : "Tap to speak"}
      title={listening ? "Stop listening" : "Tap to speak"}
      data-testid="ai-mic"
    >
      {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
    </Button>
  ) : null;
  const renderHandsFree = (compact) => (speechInputSupported ? (
    <Button
      type="button"
      variant={handsFree ? "default" : "outline"}
      size="sm"
      className={`h-10 shrink-0 rounded-xl ${compact ? "w-10 px-0 sm:w-auto sm:px-3" : "px-3"}`}
      onClick={toggleHandsFree}
      aria-pressed={handsFree}
      aria-label={handsFree ? "Turn hands-free off" : "Turn hands-free on"}
      title={handsFree ? "Turn hands-free off" : "Hands-free: keeps listening after every reply"}
      data-testid="ai-handsfree-toggle"
    >
      <AudioLines className={`h-4 w-4 ${handsFree ? "animate-pulse" : ""}`} />
      <span className={`text-xs font-semibold ${compact ? "hidden sm:inline" : ""}`}>Hands-free {handsFree ? "on" : "off"}</span>
    </Button>
  ) : null);
  const muteButton = (
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => { setMuted((m) => !m); stopSpeaking(); }} aria-label={muted ? "Unmute replies" : "Mute replies"} title={muted ? "Unmute replies" : "Mute replies"} data-testid="ai-mute-toggle">
      {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
    </Button>
  );

  const confirmCard = pending ? (
    <div className="rounded-2xl border border-amber-400/60 bg-amber-500/10 p-3 text-sm" data-testid="ai-confirm-card">
      <p className="font-semibold">{pending.summary || "Apply these changes?"}</p>
      <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto text-xs" data-testid="ai-confirm-list">
        {pending.changes.map((c, i) => <li key={i} className="flex gap-1.5"><span className="text-muted-foreground">{i + 1}.</span><span>{c}</span></li>)}
      </ul>
      {pending.problems?.length ? (
        <p className="mt-1.5 text-xs text-destructive">Skipped: {pending.problems.join("; ")}</p>
      ) : null}
      <div className="mt-2 flex gap-2">
        <Button type="button" size="sm" className="h-9 flex-1 rounded-xl bg-emerald-600 text-white hover:bg-emerald-600/90" disabled={busy} onClick={() => confirmPending(true)} data-testid="ai-confirm-apply">
          <Check className="h-4 w-4" /> Apply {pending.changes.length > 1 ? `${pending.changes.length} changes` : ""}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-9 rounded-xl" disabled={busy} onClick={() => confirmPending(false)} data-testid="ai-confirm-cancel">Cancel</Button>
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">You can also just say “yes, apply” or “no, leave it”.</p>
    </div>
  ) : null;

  // ── UI: closed ──────────────────────────────────────────────────────
  if (mode === "closed") return launcherNode;

  // ── UI: mini (voice-only pill) ──────────────────────────────────────
  if (mode === "mini") {
    return (
      <>
        {launcherNode}
        <div className="fixed right-3 top-3 z-[120] flex w-[min(94vw,460px)] flex-col gap-2 print:hidden" data-testid="ai-mini">
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-card/95 p-2 shadow-xl shadow-black/20 backdrop-blur-xl">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${statusTone} ${listening || speaking ? "animate-pulse" : ""}`} aria-hidden="true">
              <StatusIcon className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold leading-tight">Copilot</p>
              <p className="truncate text-[11px] leading-tight text-muted-foreground" data-testid="ai-status">{interim || statusText}</p>
            </div>
            {micButton}
            {renderHandsFree(true)}
            {muteButton}
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMode("full")} aria-label="Show the chat" title="Show the chat" data-testid="ai-expand">
              <MessageSquare className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={closePanel} aria-label="Close copilot" title="Close" data-testid="ai-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {confirmCard ? <div className="rounded-2xl bg-card/95 shadow-xl shadow-black/20 backdrop-blur-xl">{confirmCard}</div> : null}
        </div>
      </>
    );
  }

  // ── UI: full chat panel (anchored top-right) ────────────────────────
  return (
    <>
      {launcherNode}
      <div
        className={`fixed right-3 top-3 z-[120] flex flex-col overflow-hidden rounded-[24px] border border-border bg-card/95 shadow-2xl shadow-black/25 backdrop-blur-xl print:hidden ${large ? "h-[min(88vh,780px)] w-[min(96vw,640px)]" : "h-[min(74vh,600px)] w-[min(94vw,400px)]"}`}
        role="dialog"
        aria-label="AI copilot"
        data-testid="ai-panel"
      >
        {/* header */}
        <div className="flex items-center justify-between gap-2 border-b border-border/70 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${statusTone} ${listening || speaking ? "animate-pulse" : ""}`}><StatusIcon className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /></span>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight">Copilot</p>
              <p className="truncate text-[11px] leading-tight text-muted-foreground" data-testid="ai-status">{statusText}</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {muteButton}
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={resetConversation} aria-label="Start a new conversation" title="New conversation" data-testid="ai-reset">
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="hidden h-9 w-9 rounded-lg sm:inline-flex" onClick={() => setLarge((v) => !v)} aria-label={large ? "Smaller panel" : "Larger panel"} title={large ? "Smaller panel" : "Larger panel"} data-testid="ai-size-toggle">
              {large ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMode("mini")} aria-label="Hide the chat (voice only)" title="Hide the chat — keep talking" data-testid="ai-minimize">
              <MessageSquareOff className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={closePanel} aria-label="Close copilot" title="Close" data-testid="ai-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* voice bar */}
        {speechInputSupported ? (
          <div className="flex items-center gap-2 border-b border-border/70 bg-secondary/40 px-3 py-2" data-testid="ai-voice-bar">
            {micButton}
            {renderHandsFree(false)}
            <p className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {handsFree ? (
                <button type="button" className="inline-flex items-center gap-1 underline-offset-2 hover:text-foreground hover:underline" onClick={() => setMode("mini")} data-testid="ai-minimize-hint">
                  <MessageSquareOff className="h-3 w-3" /> Hide the chat and keep talking
                </button>
              ) : "Tap the mic once, or turn on hands-free to keep the conversation going."}
            </p>
          </div>
        ) : null}

        {/* transcript */}
        <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3" data-testid="ai-transcript">
          {messages.length === 0 ? (
            <div className="space-y-2 rounded-2xl border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="ai-empty">
              <p className="font-medium text-foreground">Try saying…</p>
              <ul className="space-y-1">
                {["Take me to the stock take", "How much did we make today?", "What's low on stock?", "Change the House Lemonade price to R25", "Add 12 bottles of tonic to stock"].map((s) => (
                  <li key={s}>
                    <button type="button" onClick={() => ask(s, "text")} className="text-left underline-offset-2 hover:text-foreground hover:underline" data-testid="ai-suggestion">{s}</button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}`}>
              <div className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[hsl(var(--primary))] text-primary-foreground" : m.error ? "border border-destructive/40 bg-destructive/10 text-foreground" : m.capped ? "border border-amber-400/60 bg-amber-500/10 text-foreground" : "bg-secondary text-foreground"}`}>
                {m.role === "user" && m.mode === "voice" ? <Mic className="mr-1 inline h-3 w-3 opacity-70" /> : null}
                {m.text || (m.streaming ? <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />{m.tools?.length ? `Working (${m.tools[m.tools.length - 1].replace(/_/g, " ")})…` : "Thinking…"}</span> : "")}
                {m.applied?.length ? (
                  <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">{m.applied.map((a, i) => <li key={i}><Check className="mr-1 inline h-3 w-3 text-emerald-600" />{a}</li>)}</ul>
                ) : null}
              </div>
            </div>
          ))}
          {interim ? (
            <div className="flex justify-end"><div className="max-w-[88%] rounded-2xl border border-dashed border-border px-3 py-2 text-sm italic text-muted-foreground" data-testid="ai-interim">{interim}</div></div>
          ) : null}
          {confirmCard}
        </div>

        {/* composer */}
        <div className="border-t border-border/70 px-3 pb-2 pt-2">
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => { e.preventDefault(); ask(input, "text"); }}
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "Listening…" : speechInputSupported ? "Type here, or use the mic above…" : "Type your question…"}
              className="h-11 rounded-xl"
              disabled={busy}
              data-testid="ai-input"
            />
            {busy ? (
              <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-xl" onClick={() => abortRef.current?.abort()} aria-label="Stop" data-testid="ai-stop">
                <Loader2 className="h-5 w-5 animate-spin" />
              </Button>
            ) : (
              <Button type="submit" size="icon" className="h-11 w-11 shrink-0 rounded-xl" disabled={!input.trim()} aria-label="Send" data-testid="ai-send">
                <Send className="h-5 w-5" />
              </Button>
            )}
          </form>
          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">{settings.tts_available && !openAiDeadRef.current ? "OpenAI voice" : "Device voice"}{settings.speak_replies === false ? " · replies not spoken" : muted ? " · muted" : ""}</span>
            {usage ? (
              <span className="inline-flex items-center gap-1.5" title={`${usage.requests || 0} requests this month`} data-testid="ai-usage-meter">
                <span className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary"><span className={`block h-full ${pctUsed >= 90 ? "bg-destructive" : pctUsed >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pctUsed}%` }} /></span>
                <span className="tabular-nums">R{usedZar.toFixed(2)}{capZar > 0 ? ` / R${capZar.toFixed(0)}` : ""}</span>
                <Badge variant="outline" className="h-4 px-1 text-[9px]">{context?.model || "gpt-4.1-mini"}</Badge>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};
